import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Alert, Snackbar } from "@mui/material";
import { createMasterApi } from "../../api/masterApi";
import {
  getOnlineClassImageUrl,
  getOnlineClassVideoUrl,
  onlineClassApi,
} from "../../api/onlineClassApi";
import {
  buildAppPath,
  getReferenceUserId,
  isAdminRole,
} from "../../utils/auth";
import "../../components/MasterCrud.css";

const usersApi = createMasterApi("/users");

function toLocalInputValue(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

const emptyForm = {
  title: "",
  description: "",
  astrologerId: "",
  scheduledStart: "",
  scheduledEnd: "",
  maxStudents: "100",
  isPaid: false,
  price: "",
  isEnrollmentRequired: true,
};

export default function OnlineClassForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [existingThumbnail, setExistingThumbnail] = useState(null);
  const [existingVideo, setExistingVideo] = useState(null);
  const [astrologers, setAstrologers] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const showToast = (message, severity = "info") => {
    setToast({ open: true, message, severity });
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (isAdminRole()) {
          const users = await usersApi.list();
          const providers = (users || []).filter((u) => {
            const code = String(u.user_role_code || "").toLowerCase();
            const role = String(u.user_role || "").toLowerCase();
            return (
              code === "service_provider" || role.includes("service provider")
            );
          });
          if (mounted) setAstrologers(providers);
        } else {
          const refId = getReferenceUserId();
          if (mounted) {
            setForm((prev) => ({ ...prev, astrologerId: String(refId || "") }));
          }
        }

        if (isEdit) {
          const item = await onlineClassApi.getOne(id);
          if (!mounted) return;
          setForm({
            title: item.title || "",
            description: item.description || "",
            astrologerId: String(item.astrologer?.id || ""),
            scheduledStart: toLocalInputValue(item.scheduledStart),
            scheduledEnd: toLocalInputValue(item.scheduledEnd),
            maxStudents: String(item.maxStudents || 100),
            isPaid: Boolean(item.isPaid),
            price: item.price != null ? String(item.price) : "",
            isEnrollmentRequired: item.isEnrollmentRequired !== false,
          });
          setExistingThumbnail(item.thumbnail);
          setExistingVideo(item.video || null);
        }
      } catch (err) {
        if (mounted) showToast(err.message || "Failed to load form", "error");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id, isEdit]);

  const onChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validate = () => {
    if (!form.title.trim()) return "Title is required";
    if (!form.description.trim()) return "Description is required";
    if (!form.scheduledStart) return "Start time is required";
    if (!form.scheduledEnd) return "End time is required";
    if (new Date(form.scheduledEnd) <= new Date(form.scheduledStart)) {
      return "End time must be after start time";
    }
    if (isAdminRole() && !form.astrologerId) return "Astrologer is required";
    if (Number(form.maxStudents) <= 0) return "Maximum students must be > 0";
    if (form.isPaid) {
      const price = Number(form.price);
      if (!Number.isFinite(price) || price < 0) {
        return "Price is required for paid classes";
      }
    }
    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      showToast(validationError, "error");
      return;
    }

    setSaving(true);
    try {
      const body = new FormData();
      body.append("title", form.title.trim());
      body.append("description", form.description.trim());
      body.append("astrologer_id", form.astrologerId);
      body.append(
        "scheduled_start",
        new Date(form.scheduledStart).toISOString()
      );
      body.append("scheduled_end", new Date(form.scheduledEnd).toISOString());
      body.append("max_students", String(form.maxStudents));
      body.append("is_paid", form.isPaid ? "1" : "0");
      body.append("price", form.isPaid ? String(form.price) : "");
      body.append(
        "is_enrollment_required",
        form.isEnrollmentRequired ? "1" : "0"
      );
      if (thumbnailFile) body.append("thumbnail", thumbnailFile);
      if (videoFile) body.append("video", videoFile);

      const saved = isEdit
        ? await onlineClassApi.update(id, body)
        : await onlineClassApi.create(body);

      showToast(isEdit ? "Class updated" : "Class created", "success");
      navigate(buildAppPath(`online-classes/${saved.id}`));
    } catch (err) {
      showToast(err.message || "Failed to save class", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="master-page">
        <div className="master-card">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="master-page">
      <div className="master-card">
        <div className="master-card__header">
          {/* <p style={{ marginBottom: 8 }}>
            <Link to={buildAppPath("online-classes")}>← Back to Online Classes</Link>
          </p> */}
          <h2>{isEdit ? "Edit Online Class" : "Create Online Class"}</h2>
          <p>
            {isEdit
              ? "Update class schedule, pricing, and enrollment settings."
              : "Create a live astrology class with schedule, thumbnail, and astrologer."}
          </p>
        </div>

        <form className="master-form" onSubmit={onSubmit}>
          <div className="master-form__grid">
            <div className="master-form__field master-form__field--full">
              <label htmlFor="oc-title">Class Title *</label>
              <input
                id="oc-title"
                value={form.title}
                onChange={(e) => onChange("title", e.target.value)}
                placeholder="Vedic Astrology for Beginners"
                required
              />
            </div>

            <div className="master-form__field master-form__field--full">
              <label htmlFor="oc-description">Description *</label>
              <textarea
                id="oc-description"
                value={form.description}
                onChange={(e) => onChange("description", e.target.value)}
                placeholder="Short description"
                rows={5}
                required
              />
            </div>

            <div className="master-form__field">
              <label htmlFor="oc-thumbnail">Thumbnail</label>
              <input
                id="oc-thumbnail"
                type="file"
                accept="image/*"
                onChange={(e) => setThumbnailFile(e.target.files?.[0] || null)}
              />
              {existingThumbnail ? (
                <div className="master-form__preview">
                  <img
                    src={getOnlineClassImageUrl(existingThumbnail)}
                    alt="Thumbnail preview"
                  />
                </div>
              ) : null}
            </div>

            <div className="master-form__field">
              <label htmlFor="oc-video">Class Video</label>
              <input
                id="oc-video"
                type="file"
                accept="video/mp4,video/webm,video/quicktime,video/*"
                onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
              />
              <p style={{ margin: "6px 0 0", fontSize: 12, color: "#666" }}>
                Students can play this video in the app. Direct download is blocked.
              </p>
              {existingVideo ? (
                <div className="master-form__preview" style={{ marginTop: 10 }}>
                  <video
                    src={getOnlineClassVideoUrl(existingVideo)}
                    controls
                    controlsList="nodownload noplaybackrate"
                    disablePictureInPicture
                    onContextMenu={(e) => e.preventDefault()}
                    style={{ width: "100%", maxHeight: 220, borderRadius: 8 }}
                  >
                    Your browser does not support video playback.
                  </video>
                </div>
              ) : null}
            </div>

            {isAdminRole() ? (
              <div className="master-form__field">
                <label htmlFor="oc-astrologer">Astrologer *</label>
                <select
                  id="oc-astrologer"
                  value={form.astrologerId}
                  onChange={(e) => onChange("astrologerId", e.target.value)}
                  required
                >
                  <option value="">Select astrologer</option>
                  {astrologers.map((user) => (
                    <option key={user.user_id} value={user.user_id}>
                      {[user.first_name, user.last_name]
                        .filter(Boolean)
                        .join(" ") || user.user_id}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            <div className="master-form__field">
              <label htmlFor="oc-start">Start *</label>
              <input
                id="oc-start"
                type="datetime-local"
                value={form.scheduledStart}
                onChange={(e) => onChange("scheduledStart", e.target.value)}
                required
              />
            </div>

            <div className="master-form__field">
              <label htmlFor="oc-end">End *</label>
              <input
                id="oc-end"
                type="datetime-local"
                value={form.scheduledEnd}
                onChange={(e) => onChange("scheduledEnd", e.target.value)}
                required
              />
            </div>

            <div className="master-form__field">
              <label htmlFor="oc-max">Maximum Students</label>
              <input
                id="oc-max"
                type="number"
                min={1}
                value={form.maxStudents}
                onChange={(e) => onChange("maxStudents", e.target.value)}
              />
            </div>

            <div className="master-form__field">
              <label>Class Type</label>
              <div className="master-form__radio-group">
                <label className="master-form__radio">
                  <input
                    type="radio"
                    name="classType"
                    checked={!form.isPaid}
                    onChange={() => onChange("isPaid", false)}
                  />
                  Free
                </label>
                <label className="master-form__radio">
                  <input
                    type="radio"
                    name="classType"
                    checked={form.isPaid}
                    onChange={() => onChange("isPaid", true)}
                  />
                  Paid
                </label>
              </div>
            </div>

            {form.isPaid ? (
              <div className="master-form__field">
                <label htmlFor="oc-price">Price *</label>
                <input
                  id="oc-price"
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.price}
                  onChange={(e) => onChange("price", e.target.value)}
                  placeholder="499"
                />
              </div>
            ) : null}

            <div className="master-form__field">
              <label>Enrollment Required</label>
              <div className="master-form__radio-group">
                <label className="master-form__radio">
                  <input
                    type="radio"
                    name="enrollmentRequired"
                    checked={form.isEnrollmentRequired}
                    onChange={() => onChange("isEnrollmentRequired", true)}
                  />
                  Yes
                </label>
                <label className="master-form__radio">
                  <input
                    type="radio"
                    name="enrollmentRequired"
                    checked={!form.isEnrollmentRequired}
                    onChange={() => onChange("isEnrollmentRequired", false)}
                  />
                  No
                </label>
              </div>
            </div>
          </div>

          <div className="master-form__actions">
            <button
              type="button"
              className="master-btn master-btn--edit"
              onClick={() => navigate(buildAppPath("online-classes"))}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="master-btn master-btn--primary"
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
